/**
 * Merchant handlers for MSW
 * Implements filtering, sorting, pagination for merchants list
 */

import { http, HttpResponse } from 'msw'
import { getMockData, toMerchantListItem, toMerchantWithMetrics } from '../data'
import {
  applyDelay,
  generateTrendSeries,
  handleErrorSimulation,
  processListRequest,
  createNotFoundResponse,
  parseFilters
} from '../utils'

// Merchant search fields
const MERCHANT_SEARCH_FIELDS = [
  'name',
  'merchantId',
  'merchantUuid',
  'legalName',
  'contact.email'
]

// Merchant sort fields
const MERCHANT_SORT_FIELDS = [
  'name',
  'registeredAt',
  'statistics.totalTransactions',
  'statistics.totalRevenue',
  'status',
  'country'
]

// Filter metadata fields
const MERCHANT_FILTER_FIELDS = [
  { field: 'status', label: 'Status' },
  { field: 'category', label: 'Category' },
  { field: 'country', label: 'Country' }
]

export const merchantHandlers = [
  /**
   * GET /api/bff/merchant/
   * NEW: List merchants with aggregated metrics and chart data
   */
  http.get('/api/bff/merchant/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/merchant/')
    if (errorResponse) return errorResponse

    // Apply delay simulation
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data
    const { merchants } = getMockData()

    // Convert to MerchantWithMetrics format
    let merchantsWithMetrics = merchants.map((m, index) => toMerchantWithMetrics(m, index))

    // Parse filters
    const filters = parseFilters(searchParams)

    // Apply soft_description filter (ilike)
    const softDescFilter = filters.find(f => f.field === 'soft_description')
    if (softDescFilter && softDescFilter.operator === 'ilike' && typeof softDescFilter.value === 'string') {
      const searchTerm = softDescFilter.value.replace(/%/g, '').toLowerCase()
      merchantsWithMetrics = merchantsWithMetrics.filter(m =>
        m.soft_description.toLowerCase().includes(searchTerm)
      )
    }

    // Apply is_blocked filter
    const isBlockedFilter = filters.find(f => f.field === 'is_blocked')
    if (isBlockedFilter) {
      if (isBlockedFilter.operator === 'in' && Array.isArray(isBlockedFilter.value)) {
        const blockedValues = isBlockedFilter.value.map(v => v === 'true')
        merchantsWithMetrics = merchantsWithMetrics.filter(m =>
          blockedValues.includes(m.is_blocked)
        )
      } else if (isBlockedFilter.operator === 'eq') {
        const blockedValue = isBlockedFilter.value === 'true'
        merchantsWithMetrics = merchantsWithMetrics.filter(m =>
          m.is_blocked === blockedValue
        )
      }
    }

    // Apply mcc filter
    const mccFilter = filters.find(f => f.field === 'mcc')
    if (mccFilter) {
      if (mccFilter.operator === 'in' && Array.isArray(mccFilter.value)) {
        merchantsWithMetrics = merchantsWithMetrics.filter(m =>
          mccFilter.value.includes(m.mcc)
        )
      } else if (mccFilter.operator === 'eq' && typeof mccFilter.value === 'string') {
        merchantsWithMetrics = merchantsWithMetrics.filter(m =>
          m.mcc === mccFilter.value
        )
      } else if (mccFilter.operator === 'ilike' && typeof mccFilter.value === 'string') {
        const searchTerm = mccFilter.value.replace(/%/g, '').toLowerCase()
        merchantsWithMetrics = merchantsWithMetrics.filter(m =>
          m.mcc.toLowerCase().includes(searchTerm)
        )
      }
    }

    // Apply date range filter on daily_data (optional - filters the daily_data array)
    const dateStart = searchParams.get('date_start')
    const dateEnd = searchParams.get('date_end')
    if (dateStart || dateEnd) {
      merchantsWithMetrics = merchantsWithMetrics.map((m) => {
        let filteredDailyData = m.daily_data
        if (dateStart) {
          filteredDailyData = filteredDailyData.filter(d => d.date >= dateStart)
        }
        if (dateEnd) {
          filteredDailyData = filteredDailyData.filter(d => d.date <= dateEnd)
        }
        return { ...m, daily_data: filteredDailyData }
      })
    }

    // Apply sorting
    const sortParam = searchParams.get('sort')
    if (sortParam) {
      const [field, direction = 'desc'] = sortParam.split(':')
      merchantsWithMetrics.sort((a, b) => {
        let aVal: number | string
        let bVal: number | string

        switch (field) {
          case 'total_volume_usd':
            aVal = a.total_volume_usd
            bVal = b.total_volume_usd
            break
          case 'transaction_count':
            aVal = a.transaction_count
            bVal = b.transaction_count
            break
          case 'soft_description':
            aVal = a.soft_description.toLowerCase()
            bVal = b.soft_description.toLowerCase()
            break
          default:
            aVal = a.id
            bVal = b.id
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return direction === 'asc' ? aVal - bVal : bVal - aVal
        }
        return direction === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal))
      })
    }

    // Apply pagination
    const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
    const limit = Math.max(1, Math.min(100, parseInt(searchParams.get('limit') || '25', 10)))
    const total = merchantsWithMetrics.length
    const pages = Math.ceil(total / limit)
    const start = (page - 1) * limit
    const end = start + limit

    return HttpResponse.json({
      page,
      limit,
      total,
      pages,
      data: merchantsWithMetrics.slice(start, end)
    })
  }),

  /**
   * GET /api/bff/merchants/
   * List merchants with filtering, sorting, and pagination
   */
  http.get('/api/bff/merchants/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/merchants/')
    if (errorResponse) return errorResponse

    // Apply delay simulation
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data
    const { merchants } = getMockData()

    // Handle additional filters not covered by processListRequest
    let filteredMerchants = [...merchants]

    // Country filter
    const country = searchParams.get('country')
    if (country) {
      const countries = country.split(',').map(c => c.trim().toUpperCase())
      filteredMerchants = filteredMerchants.filter(m =>
        countries.includes(m.country.toUpperCase())
      )
    }

    // Category filter
    const category = searchParams.get('category')
    if (category) {
      const categories = category.split(',').map(c => c.trim().toLowerCase())
      filteredMerchants = filteredMerchants.filter(m =>
        categories.includes(m.category.toLowerCase())
      )
    }

    // Process list request with filtering, sorting, pagination
    const result = processListRequest(filteredMerchants, searchParams, {
      searchFields: MERCHANT_SEARCH_FIELDS,
      filterConfig: {
        dateField: 'registeredAt'
      },
      sortFields: MERCHANT_SORT_FIELDS,
      defaultSortBy: 'registeredAt',
      defaultSortOrder: 'desc',
      filterMetadataFields: MERCHANT_FILTER_FIELDS
    })

    // Check for validation errors
    if (!result.validation.valid) {
      return HttpResponse.json({
        error: {
          code: 'VALIDATION_ERROR',
          message: 'Invalid query parameters',
          details: result.validation.errors,
          timestamp: new Date().toISOString()
        }
      }, { status: 400 })
    }

    // Convert to list items
    const merchantListItems = result.items.map(m => toMerchantListItem(m))

    // Build response
    return HttpResponse.json({
      merchants: merchantListItems,
      pagination: result.pagination,
      filters: result.filters,
      meta: result.meta
    })
  }),

  /**
   * GET /api/bff/merchants/summary/
   * Merchant summary statistics
   */
  http.get('/api/bff/merchants/summary/', async ({ request }) => {
    const url = new URL(request.url)

    // Handle error simulation
    const errorResponse = handleErrorSimulation(url.searchParams, {}, '/api/bff/merchants/summary/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data
    const { merchants } = getMockData()

    // Count by status
    const statusCounts: Record<string, number> = {}
    for (const m of merchants) {
      statusCounts[m.status] = (statusCounts[m.status] || 0) + 1
    }

    const statusDistribution = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage: Number((count / merchants.length * 100).toFixed(2))
    }))

    // Count by category
    const categoryCounts: Record<string, number> = {}
    for (const m of merchants) {
      categoryCounts[m.category] = (categoryCounts[m.category] || 0) + 1
    }

    const categoryDistribution = Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
      percentage: Number((count / merchants.length * 100).toFixed(2))
    }))

    // Count by country
    const countryCounts: Record<string, number> = {}
    for (const m of merchants) {
      countryCounts[m.country] = (countryCounts[m.country] || 0) + 1
    }

    const geographicDistribution = Object.entries(countryCounts).map(([country, count]) => ({
      country,
      count,
      percentage: Number((count / merchants.length * 100).toFixed(2))
    }))

    // Top performers by revenue
    const topPerformers = [...merchants]
      .sort((a, b) => b.statistics.totalRevenue - a.statistics.totalRevenue)
      .slice(0, 5)
      .map(m => toMerchantListItem(m))

    // Recent registrations
    const recentRegistrations = [...merchants]
      .sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime())
      .slice(0, 5)
      .map(m => toMerchantListItem(m))

    const trends = {
      totalMerchants: generateTrendSeries({ base: merchants.length, points: 14, integer: true, volatility: 0.05 }),
      activeCount: generateTrendSeries({ base: statusCounts['active'] || 0, points: 14, integer: true, volatility: 0.06 }),
      inactiveCount: generateTrendSeries({ base: statusCounts['inactive'] || 0, points: 14, integer: true, volatility: 0.06 }),
      pendingCount: generateTrendSeries({ base: statusCounts['pending'] || 0, points: 14, integer: true, volatility: 0.08 })
    }

    return HttpResponse.json({
      totalMerchants: merchants.length,
      activeCount: statusCounts['active'] || 0,
      inactiveCount: statusCounts['inactive'] || 0,
      pendingCount: statusCounts['pending'] || 0,
      trends,
      statusDistribution,
      categoryDistribution,
      geographicDistribution,
      topPerformers,
      recentRegistrations,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/merchants/:id
   * Get single merchant by ID
   */
  http.get('/api/bff/merchants/:id', async ({ params, request }) => {
    const url = new URL(request.url)
    const id = params.id as string

    // Handle error simulation
    const errorResponse = handleErrorSimulation(url.searchParams, {}, `/api/bff/merchants/${id}`)
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data
    const { merchants, wallets } = getMockData()

    // Find merchant by ID or merchantUuid
    const merchant = merchants.find(
      m => m.id === id || m.merchantUuid === id || m.merchantId === id
    )

    if (!merchant) {
      return createNotFoundResponse('Merchant', id, `/api/bff/merchants/${id}`)
    }

    // Get wallets for this merchant
    const merchantWallets = wallets
      .filter(w => w.merchantId === merchant.id)
      .map(w => ({
        id: w.id,
        walletUuid: w.wallet_uuid,
        name: w.name,
        currency: w.currency,
        balance: w.balance,
        status: w.status
      }))

    return HttpResponse.json({
      merchant,
      wallets: merchantWallets,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  })
]
